"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { X, Save, Loader2 } from "lucide-react";
import { getUserAIs, updateAIEntity } from "@/lib/ai/entityManager";
import type { AIEntity } from "@/types/aiEntity";
import { useUser } from "@/lib/user/context";
import PremiumHeroBackground from "@/components/ui/PremiumHeroBackground";

interface Props {
  aiId: string;
  mode: "edit" | "connect";
  onClose: () => void;
}

export default function AIEditInterface({ aiId, mode, onClose }: Props) {
  const router = useRouter();
  const { user } = useUser();
  const [ai, setAi] = useState<AIEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    loadAI();
  }, [aiId, user]);

  async function loadAI() {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const ais = await getUserAIs(user.id);
      const found = ais.find((a) => a.id === aiId);
      if (found) {
        setAi(found);
        setName(found.name);
        setDescription(found.description || "");
      } else {
        setError("AI not found");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load AI");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!ai || !user) return;

    setSaving(true);
    setError(null);

    try {
      await updateAIEntity(ai.id, {
        name,
        description: description || undefined,
        updatedAt: Date.now(),
      }, user.id);
      
      router.push("/builder");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <PremiumHeroBackground className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </PremiumHeroBackground>
    );
  }

  if (error && !ai) {
    return (
      <PremiumHeroBackground className="min-h-screen flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">Error</h2>
          <p className="text-white/80 mb-6">{error}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition"
          >
            Go Back
          </button>
        </div>
      </PremiumHeroBackground>
    );
  }

  if (mode === "connect") {
    return (
      <PremiumHeroBackground className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-2xl w-full">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Connect AI: {ai?.name}</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-white/80 mb-6">
            Multi-agent workflows allow your AIs to collaborate and share information.
            This feature is coming soon.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-lg text-white transition"
          >
            Close
          </button>
        </div>
      </PremiumHeroBackground>
    );
  }

  return (
    <PremiumHeroBackground className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Edit AI: {ai?.name}</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-white/80 mb-2 font-medium">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
                placeholder="AI Name"
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2 font-medium">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 resize-none"
                placeholder="Describe what this AI does..."
              />
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white transition"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg text-white transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </PremiumHeroBackground>
  );
}


